import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Globe } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import VisualRegionSelector from "@/components/onboarding/VisualRegionSelector";
import RadioStep from "@/components/onboarding/RadioStep";
import FormStep from "@/components/onboarding/FormStep";
import StepNavigation from "@/components/onboarding/StepNavigation";

const EUROPE_IMG = "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600";
const ASIA_IMG = "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=600";
const AMERICAS_IMG = "https://images.unsplash.com/photo-1526392060635-9d6019884377?w=600";
const OCEANIA_IMG = "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=600";
const ANYWHERE_IMG = "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600";

const QUESTIONS = [
  {
    id: "age_range",
    title: "What's your age range?",
    description: "Some visas have age requirements",
    options: [
      { value: "18_25", label: "18-25" },
      { value: "26_35", label: "26-35" },
      { value: "36_45", label: "36-45" },
      { value: "46_55", label: "46-55" },
      { value: "56_65", label: "56-65" },
      { value: "over_65", label: "65+" },
    ],
  },
  {
    id: "employment_status",
    title: "What's your employment status?",
    description: "This determines which visa types you qualify for",
    options: [
      { value: "remote_employee", label: "Remote Employee", desc: "Work for a company remotely" },
      { value: "non_remote_employee", label: "Non-Remote Employee", desc: "Work on-site for a company" },
      { value: "self_employed", label: "Self-Employed / Freelancer", desc: "Run your own business or freelance" },
      { value: "student", label: "Student", desc: "Currently enrolled in education" },
      { value: "retired", label: "Retired", desc: "Living on pension/retirement income" },
      { value: "unemployed", label: "Unemployed", desc: "Currently not working" },
    ],
  },
  {
    id: "monthly_income",
    title: "What's your monthly income?",
    description: "Many visas have minimum income requirements",
    options: [
      { value: "under_1000", label: "Under $1,000" },
      { value: "1000_2000", label: "$1,000 - $2,000" },
      { value: "2000_3000", label: "$2,000 - $3,000" },
      { value: "3000_5000", label: "$3,000 - $5,000" },
      { value: "5000_10000", label: "$5,000 - $10,000" },
      { value: "over_10000", label: "Over $10,000" },
    ],
  },
  {
    id: "total_savings",
    title: "What's your total savings?",
    description: "Some visas require proof of savings",
    options: [
      { value: "under_5000", label: "Under $5,000" },
      { value: "5000_20000", label: "$5,000 - $20,000" },
      { value: "20000_50000", label: "$20,000 - $50,000" },
      { value: "50000_100000", label: "$50,000 - $100,000" },
      { value: "over_100000", label: "Over $100,000" },
    ],
  },
  {
    id: "preferred_regions",
    title: "Where do you dream of living?",
    description: "Select all regions that inspire you",
    type: "visual_regions",
    options: [
      { value: "europe", label: "Europe", image: EUROPE_IMG, subtitle: "Portugal, Spain, France & more" },
      { value: "asia", label: "Asia", image: ASIA_IMG, subtitle: "Thailand, Japan, Vietnam & more" },
      { value: "americas", label: "Americas", image: AMERICAS_IMG, subtitle: "Mexico, Costa Rica, Colombia & more" },
      { value: "oceania", label: "Oceania", image: OCEANIA_IMG, subtitle: "Australia, New Zealand" },
      { value: "anywhere", label: "Anywhere", image: ANYWHERE_IMG, subtitle: "Show me all options" },
    ],
  },
  {
    id: "timeline",
    title: "When do you want to move?",
    description: "This helps prioritize your options",
    options: [
      { value: "asap", label: "ASAP (0-3 months)" },
      { value: "3_6_months", label: "3-6 months" },
      { value: "6_12_months", label: "6-12 months" },
      { value: "exploring", label: "Just exploring" },
    ],
  },
];

const OPTIONAL_STEPS = [
  {
    id: "passport_details",
    title: "Passport & Personal Details",
    description: "Optional — helps pre-fill visa applications",
    type: "form",
    skippable: true,
    fields: [
      { key: "passport_full_name", label: "Full Name (as in passport)", placeholder: "Surname(s), Given Name(s)", type: "text" },
      { key: "place_of_birth", label: "Place of Birth", placeholder: "City, Country", type: "text" },
      { key: "sex", label: "Sex", type: "select", options: [{ value: "male", label: "Male" }, { value: "female", label: "Female" }, { value: "other", label: "Other" }] },
      { key: "passport_issue_date", label: "Passport Issue Date", type: "date" },
      { key: "passport_expiry_date", label: "Passport Expiry Date", type: "date" },
      { key: "passport_issuing_authority", label: "Issuing Authority", placeholder: "e.g., U.S. Department of State", type: "text" },
      { key: "email_address", label: "Email Address", placeholder: "your@email.com", type: "email" },
    ],
  },
  {
    id: "residence_history",
    title: "Residence & History",
    description: "Optional — needed for consulate selection and criminal record certificates",
    type: "form",
    skippable: true,
    fields: [
      { key: "residence_country", label: "Country of Residence", placeholder: "e.g., United States", type: "text" },
      { key: "residence_city", label: "City of Residence", placeholder: "e.g., New York", type: "text" },
      { key: "countries_last_5_years", label: "Countries Lived in (Last 5 Years)", type: "country_list" },
    ],
  },
  {
    id: "work_insurance",
    title: "Employment, Insurance & Accommodation",
    description: "Optional — for employer letter and insurance requirements",
    type: "form",
    skippable: true,
    fields: [
      { key: "employer_country", label: "Employer Country", placeholder: "e.g., United States", type: "text" },
      { key: "employer_address", label: "Employer Address", placeholder: "Full address", type: "text" },
      { key: "employer_hr_contact", label: "HR / Manager Contact", placeholder: "Name, email, phone", type: "text" },
      { key: "health_insurance_provider", label: "Health Insurance Provider", placeholder: "e.g., Blue Cross Blue Shield", type: "text" },
      { key: "health_insurance_will_purchase", label: "Will purchase compliant insurance abroad?", type: "checkbox" },
      { key: "years_experience", label: "Years of Professional Experience", placeholder: "e.g., 5", type: "text" },
      { key: "accommodation_type", label: "Accommodation Document Type", type: "select", options: [{ value: "lease", label: "Lease Agreement" }, { value: "booking", label: "Hotel/Airbnb Booking" }, { value: "invitation", label: "Invitation Letter from Host" }, { value: "undecided", label: "Not Yet Decided" }] },
    ],
  },
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const totalSteps = 9;

  const [profile, setProfile] = useState({
    age_range: "", employment_status: "", monthly_income: "", total_savings: "",
    preferred_regions: [], timeline: "",
    passport_full_name: "", place_of_birth: "", sex: "",
    passport_issue_date: "", passport_expiry_date: "", passport_issuing_authority: "",
    email_address: "", residence_country: "", residence_city: "",
    countries_last_5_years: [], employer_country: "", employer_address: "",
    employer_hr_contact: "", health_insurance_provider: "",
    health_insurance_will_purchase: false, years_experience: "", accommodation_type: "",
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadExistingProfile();
  }, []);

  const loadExistingProfile = async () => {
    try {
      const existing = await api.getProfile();
      if (existing && existing.onboarding_completed) {
        setProfile((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.keys(prev).map((k) => [k, existing[k] ?? prev[k]])
          ),
        }));
      }
    } catch {
      // Expected on first visit
    }
  };

  const isOptionalStep = step > 6;
  const currentQuestion = step <= 6 ? QUESTIONS[step - 1] : null;
  const currentOptionalStep = isOptionalStep ? OPTIONAL_STEPS[step - 7] : null;

  const handleSingleSelect = (value) => {
    setProfile({ ...profile, [currentQuestion.id]: value });
  };

  const handleMultiSelect = (value) => {
    const current = profile.preferred_regions || [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setProfile({ ...profile, preferred_regions: updated });
  };

  const handleFieldChange = (key, value) => {
    setProfile((prev) => ({ ...prev, [key]: value }));
  };

  const addCountryEntry = () => {
    setProfile((prev) => ({
      ...prev,
      countries_last_5_years: [...(prev.countries_last_5_years || []), { country: "", from_date: "", to_date: "" }],
    }));
  };

  const updateCountryEntry = (index, field, value) => {
    setProfile((prev) => {
      const updated = [...(prev.countries_last_5_years || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, countries_last_5_years: updated };
    });
  };

  const removeCountryEntry = (index) => {
    setProfile((prev) => ({
      ...prev,
      countries_last_5_years: (prev.countries_last_5_years || []).filter((_, i) => i !== index),
    }));
  };

  const submitProfile = async () => {
    setIsLoading(true);
    try {
      await api.updateProfile({ ...profile, onboarding_completed: true });
      toast.success("Profile complete! Let's see your options.");
      navigate("/results");
    } catch {
      toast.error("Failed to save profile");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      submitProfile();
    }
  };

  const handleSkip = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      submitProfile();
    }
  };

  const canProceed = () => {
    if (isOptionalStep) return true;
    const value = profile[currentQuestion.id];
    if (currentQuestion.type === "visual_regions") return value && value.length > 0;
    return !!value;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-secondary/50 to-background flex flex-col">
      <header className="p-4 md:p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg font-['Manrope']">Nomad Roadmap</span>
          </div>
        </div>
      </header>

      <div className="px-4 md:px-8 max-w-3xl mx-auto w-full">
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
          <span>Step {step} of {totalSteps}</span>
          <span>{Math.round((step / totalSteps) * 100)}%</span>
        </div>
        <Progress value={(step / totalSteps) * 100} className="h-2" />
      </div>

      <main className="flex-1 flex items-center justify-center p-4">
        <Card
          className={`w-full border-border/50 shadow-xl animate-fade-in ${
            currentQuestion?.type === "visual_regions" ? "max-w-3xl" : "max-w-xl"
          }`}
          key={step}
        >
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-2xl font-bold font-['Manrope']">
              {isOptionalStep ? currentOptionalStep.title : currentQuestion.title}
            </CardTitle>
            <CardDescription>
              {isOptionalStep ? currentOptionalStep.description : currentQuestion.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isOptionalStep ? (
              <FormStep
                stepConfig={currentOptionalStep}
                profile={profile}
                onFieldChange={handleFieldChange}
                onAddCountryEntry={addCountryEntry}
                onUpdateCountryEntry={updateCountryEntry}
                onRemoveCountryEntry={removeCountryEntry}
              />
            ) : currentQuestion.type === "visual_regions" ? (
              <VisualRegionSelector
                options={currentQuestion.options}
                selectedRegions={profile.preferred_regions}
                onToggle={handleMultiSelect}
              />
            ) : (
              <RadioStep
                question={currentQuestion}
                selectedValue={profile[currentQuestion.id]}
                onSelect={handleSingleSelect}
              />
            )}

            <StepNavigation
              step={step}
              totalSteps={totalSteps}
              isOptionalStep={isOptionalStep}
              canProceed={canProceed()}
              isLoading={isLoading}
              onBack={() => step > 1 && setStep(step - 1)}
              onNext={handleNext}
              onSkip={handleSkip}
            />
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default OnboardingPage;